import { useMemo, useState, type ReactElement } from 'react'
import { confirmDelete } from '@util'
import { type CompanyId, isDefined, type QuestionId } from '@terros/common'
import { Link, useNavigate, useParams, createFileRoute } from '@tanstack/react-router'
import { useIsFetching, useQueryClient } from '@tanstack/react-query'
import { showNotification } from '@mantine/notifications'
import { useQueryDisclosure, useToastError } from '@hooks'
import {
  getQuestionReportKpiQueryKey,
  useProfile,
  useQuestion,
  useRemoveQuestion,
  useTranslation,
  useUpdateQuestion,
} from '@front/model'
import { ButtonDelete, NotReady, Page, QuestionStatusBadge, TButton } from '@components'
import { QuestionResultsDisplay } from './QuestionResultsDisplay'
import { QuestionDetailsDrawer } from './QuestionDetailsDrawer'

function QuestionDetailPage(): ReactElement {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { questionId } = useParams({ strict: false }) as { questionId: QuestionId }
  const { data: question, isLoading, error, refetch, isFetching } = useQuestion(questionId!)
  const { isTantalim } = useProfile()
  const [selectedCompanyId, setSelectedCompanyId] = useState<CompanyId | undefined>(undefined)
  const [detailsOpened, { open: openDetails, close: closeDetails }] = useQueryDisclosure('details')
  const questionReportKpiQueryKey = getQuestionReportKpiQueryKey(question ?? { questionId: questionId! })
  const reportKpiFetchCount = useIsFetching({ queryKey: questionReportKpiQueryKey })
  const assignedCompanies = useMemo(() => {
    const companies =
      question?.rules
        ?.filter((rule) => rule.value === true)
        .map((rule) => rule.company)
        .filter(isDefined) ?? []

    return new Map(companies.map((company) => [company.companyId, company]))
  }, [question?.rules])
  const companyIdFromRules = useMemo(() => {
    if (!isTantalim) return undefined
    if (assignedCompanies.size !== 1) return undefined
    return Array.from(assignedCompanies.keys()).at(0)
  }, [isTantalim, assignedCompanies])
  const showCompanyPicker = isTantalim && assignedCompanies.size !== 1
  const companyId = selectedCompanyId ?? companyIdFromRules
  const selectedCompany = companyId ? assignedCompanies.get(companyId) : undefined
  const { remove, isRemoving, error: removeError } = useRemoveQuestion()
  useToastError(removeError)
  const { update, isUpdating: isRestoring, error: restoreError } = useUpdateQuestion()
  useToastError(restoreError)

  const handleMoveToTrash = async (): Promise<void> => {
    const confirmed = await confirmDelete({
      title: t('Move Question to Trash'),
      message: t('You can restore this question anytime from the Trash.'),
      label: t('Move to Trash'),
      t,
    })
    if (!confirmed) return

    await remove({ questionId: questionId! })
    navigate({ to: '/questions' })
    showNotification({
      title: t('Question Moved to Trash'),
      message: t('You can restore this question anytime from the Trash.'),
      color: 'green',
    })
  }

  if (isLoading) return <NotReady id='Question' isLoading={isLoading} />
  if (error || !question) return <NotReady data={question} error={error} id='Question' />

  const handleRestore = async (): Promise<void> => {
    await update({ question: { ...question, isDeleted: false } })
    showNotification({ color: 'green', message: t('Question restored') })
  }

  const handleRefetch = (): void => {
    refetch()
    queryClient.refetchQueries({ queryKey: questionReportKpiQueryKey })
  }

  return (
    <Page>
      <Page.Heading
        back
        isFetching={isFetching || reportKpiFetchCount > 0}
        refetch={handleRefetch}
        title={question.text}
      >
        <QuestionStatusBadge question={question} />
        <TButton variant='secondary' onClick={openDetails}>
          {t('Details')}
        </TButton>
        <TButton component={Link} to='edit' variant='secondary'>
          {t('Edit')}
        </TButton>
        {question.isDeleted ? (
          <TButton loading={isRestoring} onClick={handleRestore}>
            {t('Restore')}
          </TButton>
        ) : (
          <ButtonDelete loading={isRemoving} onClick={handleMoveToTrash}>
            {t('Move to Trash')}
          </ButtonDelete>
        )}
      </Page.Heading>
      <Page.Content scrollArea>
        <QuestionResultsDisplay
          question={question}
          isTantalim={isTantalim}
          companyProps={{
            companyId,
            selectedCompany,
            setCompanyId: showCompanyPicker ? setSelectedCompanyId : undefined,
            companyFilter:
              assignedCompanies.size > 0 ? (company) => assignedCompanies.has(company.companyId) : undefined,
          }}
        />
        <QuestionDetailsDrawer opened={detailsOpened} question={question} onClose={closeDetails} />
      </Page.Content>
    </Page>
  )
}

export const Route = createFileRoute('/questions_/$questionId/')({
  component: QuestionDetailPage,
})
